// Buscas do bloco "Saúde do sistema" da Tecnologia (#6508). A regra de cada estado está em
// src/lib/visaoGeral/saudeSistema.ts. WhatsApp, robô e e-mail são lidos direto das tabelas
// (o time da Tecnologia é adm e membro do Inbox); os agendamentos do banco e os erros das
// funções vêm da edge `saude-sistema`. `versao` muda quando a pessoa pede para atualizar.
import { supabase } from '../../src/lib/supabase';
import type {
  ConexaoDoInbox, EmailDeCobranca, EnvioDoInbox, LinhaDaFila, RespostaSaudeFuncoes,
} from '../../src/lib/visaoGeral/saudeSistema';
import { paginar, useBusca } from './useTecnologiaMarketing';

const horasAtras = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();

/** Conexões do Inbox (WhatsApp e e-mail), com a marca de queda do monitor. */
export const useConexoesDoInbox = (versao: number) => useBusca<ConexaoDoInbox[]>(async () => {
  const { data, error } = await supabase.from('inbox_channel_connections')
    .select('id, channel_type, display_name, status, last_error, last_seen_at, atendente_id, caiu_em');
  if (error) throw error;
  return (data || []) as ConexaoDoInbox[];
}, [versao], 'Conexões do Inbox');

/** Mensagens de saída que falharam ou continuam na fila (últimos 7 dias). */
export const useEnviosComProblema = (versao: number) => useBusca<EnvioDoInbox[]>(() =>
  paginar<EnvioDoInbox>(() => supabase.from('inbox_messages')
    .select('id, status, created_at, failure_reason')
    .eq('direction', 'outbound')
    .in('status', ['failed', 'queued'])
    .gte('created_at', horasAtras(24 * 7))
    .order('id')), [versao], 'Envios do Inbox');

/**
 * Fila do robô da Equatorial: o que está aberto (pendente/processando), o que andou nas
 * últimas 24 h e a última tentativa (falha ou em andamento), de qualquer época.
 */
export const useFilaDoRoboAgora = (versao: number) => useBusca<{ fila: LinhaDaFila[]; ultimaAtividade: string | null }>(async () => {
  const colunas = 'id, status, error_details, updated_at';
  const [abertas, doDia, ultima] = await Promise.all([
    paginar<LinhaDaFila>(() => supabase.from('fatura_download_queue').select(colunas)
      .in('status', ['pendente', 'processando']).order('id')),
    paginar<LinhaDaFila>(() => supabase.from('fatura_download_queue').select(colunas)
      .in('status', ['falhou', 'concluido']).gte('updated_at', horasAtras(24)).order('id')),
    supabase.from('fatura_download_queue').select('updated_at')
      .in('status', ['falhou', 'processando']).order('updated_at', { ascending: false }).limit(1),
  ]);
  if (ultima.error) throw ultima.error;
  return { fila: [...abertas, ...doDia], ultimaAtividade: (ultima.data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? null };
}, [versao], 'Fila do robô');

/** E-mails de cobrança dos últimos 7 dias (para o "último envio" quando o dia está vazio). */
export const useEmailsDeCobranca = (versao: number) => useBusca<EmailDeCobranca[]>(() =>
  paginar<EmailDeCobranca>(() => supabase.from('faturas')
    .select('id, email_cobranca_status, email_cobranca_erro, email_cobranca_em')
    .gte('email_cobranca_em', horasAtras(24 * 7))
    .order('id')), [versao], 'E-mails de cobrança');

/** Agendamentos do banco e erros das funções (edge `saude-sistema`). */
export const useSaudeDasFuncoes = (versao: number) => useBusca<RespostaSaudeFuncoes>(async () => {
  const { data, error } = await supabase.functions.invoke('saude-sistema', { body: {} });
  if (error) {
    const detalhe = await (error as any)?.context?.json?.().catch(() => null);
    throw new Error(detalhe?.error || error.message);
  }
  return data as RespostaSaudeFuncoes;
}, [versao], 'Saúde das funções');
